'use client'

import {
  type CSSProperties,
  type ReactNode,
  useRef,
  useState,
  useLayoutEffect,
} from 'react'
import { Desktop } from '../MediaQuery/MediaQuery'
import { motion, useMotionValueEvent, useScroll } from 'framer-motion'
import { theme } from '@/styles/theme.css'
import Button from '@shared/components/Button/Button'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { Row } from '@christiankaindl/lyts'
import * as styles from './Reel.css'

const transition = { visualDuration: 0.3, bounce: 0.1, type: 'spring' } as const
export default function Reel({
  children,
  style = {},
  innerStyle = {},
  ...props
}: {
  children?: ReactNode
  style?: CSSProperties
  innerStyle?: CSSProperties
}) {
  const ref = useRef<HTMLDivElement>(null)
  const scroll = useScroll({ container: ref })
  const [arrows, setArrows] = useState<'both' | 'left' | 'right' | 'none'>(
    'right',
  )

  function updateArrows(value: number) {
    const el = ref.current
    if (!el) return

    let next: typeof arrows
    const maxScroll = el.scrollWidth - el.clientWidth - 8
    if (value > 8 && value < maxScroll) {
      next = 'both'
    } else if (value > 8) {
      next = 'left'
    } else if (value < maxScroll) {
      next = 'right'
    } else {
      next = 'none'
    }

    setArrows(next)
    el.style.setProperty('--reel-mask-right', next === 'both' || next === 'right' ? null : '0')
    el.style.setProperty('--reel-mask-left', next === 'both' || next === 'left' ? null : '0')
  }

  useMotionValueEvent(scroll.scrollX, 'change', (value) => {
    updateArrows(value)
  })

  /* eslint-disable react-hooks/set-state-in-effect -- useLayoutEffect is correct: synchronous DOM measurement + state before paint */
  useLayoutEffect(() => {
    updateArrows(scroll.scrollX.get())
  }, [ref, scroll.scrollX])
  /* eslint-enable react-hooks/set-state-in-effect */

  function nextItem() {
    ref.current?.scrollBy({ left: 300, behavior: 'smooth' })
  }
  function prevItem() {
    ref.current?.scrollBy({ left: -300, behavior: 'smooth' })
  }

  const showLeftArrow = arrows === 'both' || arrows === 'left'
  const showRightArrow = arrows === 'both' || arrows === 'right'

  return (
    <div
      style={{
        marginLeft: '-1.5em',
        marginRight: '-1.5em',
        backgroundColor: theme.colors.backgrounds.bgA1,
        borderRadius: 24,
        position: 'relative',
        ...style,
      }}
      {...props}
    >
      <Desktop>
        <motion.div
          style={{
            position: 'absolute',
            left: 9,
            zIndex: 10,
            top: '50%',
            y: '-50%',
          }}
          transition={transition}
          initial={{ scale: 0 }}
          animate={showLeftArrow ? { scale: 1 } : undefined}
        >
          <Button
            onClick={() => {
              prevItem()
            }}
            circular
            square
            size='large'
            style={{ backdropFilter: 'blur(10px)' }}
          >
            <ChevronLeftIcon size={20} />
          </Button>
        </motion.div>
        <motion.div
          style={{
            position: 'absolute',
            right: 9,
            zIndex: 10,
            top: '50%',
            y: '-50%',
          }}
          transition={transition}
          initial={{ scale: 0 }}
          animate={showRightArrow ? { scale: 1 } : undefined}
        >
          <Button
            onClick={() => {
              nextItem()
            }}
            circular
            square
            size='large'
            style={{ backdropFilter: 'blur(10px)' }}
          >
            <ChevronRightIcon size={20} />
          </Button>
        </motion.div>
      </Desktop>
      <Row
        className={styles.root}
        style={{
          // @ts-expect-error CSS custom var
          '--reel-mask-right': 0,
          '--reel-mask-left': 0,
          ...innerStyle,
        }}
        gap={1.5}
        yAlign='start'
        ref={ref}
      >
        {children}
      </Row>
    </div>
  )
}
